import React, { useCallback, useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import DomainBreadcrumbs from 'components/DomainBreadcrumbs';
import ListEmptyState from 'components/condition-list/ListEmptyState';
import {
  FinancialYearMonthResult,
  FinancialYearResult,
  ListFinancialYearResponse,
  createNextFinancialYear,
  listFinancialYear,
} from 'stores/FinancialYearStoreAdapter';
import { errorDefault, successDefault } from 'utils/Toastify';

const FinancialYearManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [data, setData] = useState<ListFinancialYearResponse | null>(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    listFinancialYear()
      .then((result) => {
        setData(result);
        setLoading(false);
      })
      .catch(() => {
        errorDefault('年度情報の取得に失敗しました');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateNextYear = () => {
    setCreating(true);
    createNextFinancialYear()
      .then(() => {
        successDefault('次年度を登録しました');
        fetchData();
        setCreating(false);
      })
      .catch((e: Error) => {
        errorDefault('次年度の登録に失敗しました', e.message);
        setCreating(false);
      });
  };

  if (loading) {
    return (
      <Box sx={{ pt: 3, pl: 2, pr: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 3, pl: 2, pr: 2 }}>
      <DomainBreadcrumbs
        items={[{ label: '管理者メニュー', to: '/management' }, { label: '年度管理' }]}
      />
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight="bold">
            年度管理
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            disabled={creating}
            onClick={handleCreateNextYear}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            次年度を登録
          </Button>
        </Stack>

        {data && data.results.length > 0 && (
          <Stack spacing={2}>
            {data.results.map((yearData) => (
              <FinancialYearTable
                key={yearData.financialYear}
                yearData={yearData}
                current={data.current}
              />
            ))}
          </Stack>
        )}

        {data && data.results.length === 0 && (
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
            <ListEmptyState
              icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 24 }} />}
              title="年度データがありません"
              description="「次年度を登録」から最初の年度を登録してください。"
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
};

type FinancialYearTableProps = {
  yearData: FinancialYearResult;
  current: FinancialYearMonthResult | null;
};

const FinancialYearTable: React.FC<FinancialYearTableProps> = ({ yearData, current }) => {
  const isCurrent = (month: FinancialYearMonthResult): boolean => {
    if (!current) return false;
    return current.financialYear === month.financialYear && current.month === month.month;
  };

  return (
    <TableContainer
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Table size="small">
        <TableBody>
          <TableRow>
            <TableCell
              sx={{
                fontWeight: 600,
                borderRight: 1,
                borderColor: 'divider',
                whiteSpace: 'nowrap',
                bgcolor: 'action.hover',
                width: 80,
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {yearData.financialYear}期
              </Typography>
            </TableCell>
            {yearData.months.map((month) => (
              <TableCell
                key={`${month.financialYear}-${month.month}`}
                align="center"
                sx={{
                  borderRight: 1,
                  borderColor: 'divider',
                  bgcolor: isCurrent(month) ? 'primary.main' : 'transparent',
                  color: isCurrent(month) ? 'primary.contrastText' : 'text.primary',
                  transition: 'background-color 0.2s',
                  px: 1,
                  minWidth: 48,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={isCurrent(month) ? 600 : undefined}
                  sx={{
                    color: isCurrent(month) ? 'primary.contrastText' : 'text.primary',
                  }}
                >
                  {month.month}月
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FinancialYearManagement;
